import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import {
  RUN_ALL_TASKS_BY_PRODUCT_ID,
  RUN_ALL_TASKS_BY_ARTICLE_ID,
} from "../../graphql/mutations";
import styles from "./SelectedActionModal.module.css";
import SelectedActionTasks from "./SelectedActionTasks";

/**
 * Normalize a string (lowercase + trim)
 * @param {string} str - The string to normalize
 * @returns {string} Normalized string
 */
const norm = (str) => (str || "").toLowerCase().trim();

/**
 * Check if an action is a Blog action by looking for [Blog] in the title
 * @param {Object} action - The action object
 * @returns {boolean} True if it's a Blog action
 */
const isBlogAction = (action = {}) => /\[Blog\]/i.test(action?.title || "");

/**
 * Check if an action should be grouped as a Product Intel action
 * @param {Object} action - The action object
 * @returns {boolean} True if it matches Product Intel criteria
 */
const shouldGroupProductIntelAction = (action = {}) =>
  Boolean(action?.product_id) &&
  (norm(action?.department) === "product_intel" ||
    norm(action?.focusArea).includes("pdp optimization") ||
    norm(action?.focusArea).includes("product"));

const extractProductTitleFromAction = (action = {}) => {
  const direct =
    action?.product_name ||
    action?.productTitle ||
    action?.product_title ||
    action?.product?.title;
  if (direct) return String(direct).trim();

  const descriptionMatch = String(action?.description || "").match(
    /^Product:\s*(.+)$/im,
  );
  if (descriptionMatch?.[1]) return descriptionMatch[1].trim();

  const title = String(action?.title || "").replace(/^\s*\[Product\]\s*/i, "");
  const colonIndex = title.indexOf(":");
  if (colonIndex !== -1 && colonIndex < title.length - 1) {
    return title.substring(colonIndex + 1).trim();
  }
  return title.trim();
};

/**
 * Extracts article title for Blog actions.
 * 1. Strip [Blog] prefix from title
 * 2. Take everything BEFORE the last colon (:) as the article title
 * @param {Object} action - The action object
 * @returns {string|null} The article title or null if not applicable
 */
const extractBlogArticleTitle = (action = {}) => {
  const raw = String(action?.title || "");

  // Step A: Strip "[Blog]" prefix
  const stripped = raw.replace(/^\s*\[Blog\]\s*/i, "");

  // Step B: Find the LAST colon
  const lastColon = stripped.lastIndexOf(":");

  // Step C: Take everything BEFORE the last colon
  if (lastColon > -1) {
    return stripped.slice(0, lastColon).trim();
  }

  // Fallback: Return entire stripped title if no colon
  return stripped.trim();
};

/**
 * Gets the action title for display in the accordion.
 * 1. For Product Intel actions: returns the FIRST part after splitting by ":"
 * 2. For all other actions: returns the LAST part after splitting by ":"
 * 3. Strips "[Blog]" prefix if present
 * E.g., "Product: ABC Product: Some Action" (Product Intel) -> "Product"
 * E.g., "Product: ABC Product: Some Action" (Other) -> "Some Action"
 * E.g., "[Blog] Article: Task Name" -> "Task Name"
 * @param {Object|string} actionOrTitle - The action object or action title string
 * @returns {string} The display title
 */
const getActionTitle = (actionOrTitle) => {
  // Handle both action object and plain string for backward compatibility
  let actionTitle = "";
  let isProductIntel = false;

  if (typeof actionOrTitle === "object" && actionOrTitle !== null) {
    actionTitle = actionOrTitle.title || "";
    isProductIntel = shouldGroupProductIntelAction(actionOrTitle);
  } else {
    actionTitle = actionOrTitle;
  }

  if (!actionTitle || typeof actionTitle !== "string") return actionTitle;

  // Strip [Blog] prefix if present
  let cleaned = actionTitle.replace(/^\s*\[Blog\]\s*/i, "").trim();

  // Split by colon
  const parts = cleaned.split(":");

  if (parts.length > 1) {
    // For Product Intel actions: return FIRST part
    // For all other actions: return LAST part
    return isProductIntel ? parts[0].trim() : parts[parts.length - 1].trim();
  }

  return cleaned;
};

/**
 * Gets the group key for an action.
 * Priority:
 * 1. group_title if it exists
 * 2. For Blog actions: extract from action title (strip [Blog], take before last colon)
 * 3. For Product Intel actions: extract title after first colon
 * 4. For all other actions: group together without a title
 * @param {Object} action - The action object
 * @returns {Object} Object containing groupKey and groupTitle
 */
const getGroupInfo = (action) => {
  // 1. If group_title exists, use it
  if (action.group_title) {
    return {
      groupKey: `group_title_${action.group_title}`,
      groupTitle: action.group_title,
    };
  }

  // 2. Handle Blog actions: extract article title from action title
  if (isBlogAction(action)) {
    const articleTitle = extractBlogArticleTitle(action);
    if (articleTitle) {
      // Use normalized article title for key (lowercase & trimmed for consistency)
      const normalizedKey = norm(articleTitle);
      return {
        groupKey: `blog-${normalizedKey}`,
        groupTitle: articleTitle,
      };
    }
  }

  // 3. Handle product actions: group by product_id so PDP description,
  // imagery, alt text, and reindex actions stay under one product row.
  if (shouldGroupProductIntelAction(action)) {
    const productId = String(action?.product_id || "").trim();
    const productName = extractProductTitleFromAction(action);
    if (productId || productName) {
      return {
        groupKey: `product-${productId || norm(productName)}`,
        groupTitle: productName || "Product",
      };
    }
  }

  // 4. For all other actions (not Blog, not Product Intel): group together without a title
  return {
    groupKey: "other",
    groupTitle: null,
  };
};

/**
 * Groups actions by their group info (group_title or extracted from task_title)
 * @param {Array} actions - Array of action objects
 * @returns {Object} Object with groupKey as keys and array of actions as values, plus groupTitles
 */
const groupActions = (actions) => {
  const groups = {};
  const groupTitles = {};
  actions.forEach((action) => {
    const { groupKey, groupTitle } = getGroupInfo(action);
    if (!groups[groupKey]) {
      groups[groupKey] = [];
      groupTitles[groupKey] = groupTitle;
    }
    groups[groupKey].push(action);
  });
  return { groups, groupTitles };
};

const actionMatchesQuery = (action = {}, query = "") => {
  const q = norm(query);
  if (!q) return true;

  const directFields = [
    action?.title,
    action?.group_title,
    action?.department,
    action?.focusArea,
    action?.product_name,
    action?.article_title,
    action?.blog_title,
    action?.description,
    action?.task_title,
    action?.task_description,
  ];

  const directMatch = directFields.some((value) => norm(String(value || "")).includes(q));
  if (directMatch) return true;

  const tasks = Array.isArray(action?.tasks) ? action.tasks : [];
  return tasks.some((task) =>
    [
      task?.title,
      task?.name,
      task?.task_title,
      task?.description,
      task?.task_description,
      task?.product_name,
      task?.article_title,
      task?.blog_title,
    ].some((value) => norm(String(value || "")).includes(q))
  );
};

const SelectedActionItem = ({
  action,
  groupActions = [],
  taskCount,
  isExpanded,
  onToggle,
  activeTab,
  onClose,
  onActionCompleted,
  onRunTask,
}) => {
  const { t } = useLocalization();
  const isSuggestedTab = activeTab === t("ZeqiKeyFocusArea.tabs.suggested");
  const isRunningTab = activeTab === t("ZeqiKeyFocusArea.tabs.running");

  // Get the action title (first part for Product Intel, last part for others)
  const displayTitle = getActionTitle(action) || action.department || "Action";

  return (
    <div
      className={`${styles.actionItem} ${isExpanded ? styles.actionItemExpanded : ""}`}
    >
      <div className={styles.actionToggle}>
        <button
          type="button"
          className={styles.actionToggleButton}
          onClick={onToggle}
          aria-expanded={isExpanded}
        >
          <div className={styles.actionHeader}>
            <span className={styles.actionTitle}>{displayTitle}</span>
            <span className={styles.actionCount}>{taskCount}</span>
          </div>
        </button>
      </div>

      {isExpanded && (
        <div className={styles.accordionContent}>
          <SelectedActionTasks
            action={action}
            groupActions={groupActions}
            isInline={true}
            enableTaskActions={isSuggestedTab || isRunningTab}
            activeTab={activeTab}
            onClose={onClose}
            onActionCompleted={onActionCompleted}
            onRunTask={onRunTask}
          />
        </div>
      )}
    </div>
  );
};

const SelectedActionProductGroup = ({
  groupKey,
  title,
  actions = [],
  isExpanded,
  onToggle,
  activeTab,
  onClose,
  onActionCompleted,
  onRunTask,
}) => {
  const { t } = useLocalization();
  const isSuggestedTab = activeTab === t("ZeqiKeyFocusArea.tabs.suggested");
  const isRunningTab = activeTab === t("ZeqiKeyFocusArea.tabs.running");
  const taskCount = actions.reduce(
    (sum, action) => sum + (Array.isArray(action?.tasks) ? action.tasks.length : 0),
    0,
  );

  return (
    <div
      className={`${styles.actionItem} ${isExpanded ? styles.actionItemExpanded : ""}`}
    >
      <div className={styles.actionToggle}>
        <button
          type="button"
          className={styles.actionToggleButton}
          onClick={onToggle}
          aria-expanded={isExpanded}
        >
          <div className={styles.actionHeader}>
            <span className={styles.actionTitle}>{title || "Product"}</span>
            <span className={styles.actionCount}>{taskCount || actions.length}</span>
          </div>
        </button>
      </div>

      {isExpanded && (
        <div className={styles.accordionContent}>
          <div className={styles.groupedActionMeta}>
            {actions.length} {actions.length === 1 ? "action" : "actions"} ·{" "}
            {taskCount} {taskCount === 1 ? "task" : "tasks"}
          </div>
          {actions.map((action) => (
            <div key={`${groupKey}-${action.id}`} className={styles.groupedActionBlock}>
              <p className={styles.groupedActionTitle}>
                {getActionTitle(action) || action.title || "Action"}
              </p>
              <SelectedActionTasks
                action={action}
                groupActions={actions}
                isInline={true}
                enableTaskActions={isSuggestedTab || isRunningTab}
                activeTab={activeTab}
                onClose={onClose}
                onActionCompleted={onActionCompleted}
                onRunTask={onRunTask}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SelectedActionModal = ({
  isOpen,
  row,
  onClose,
  activeTab,
  onUpdateRow,
  onActionCompleted,
  expandActionId,
  onRunTask,
  onRunAllStarted,
  initialSearchQuery = "",
}) => {
  const safeRow = row || { actions: [], focusArea: "" };
  const { t } = useLocalization();
  const { user } = useAuthUser();
  const authToken = user?.token;
  const userId = user?._id || user?.id;

  const isSuggestedTab = activeTab === t("ZeqiKeyFocusArea.tabs.suggested");
  const isRunningTab = activeTab === t("ZeqiKeyFocusArea.tabs.running");
  const [expandedActionId, setExpandedActionId] = useState(null);
  const [loadingGroupId, setLoadingGroupId] = useState(null);
  const [runAllTriggered, setRunAllTriggered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const hasAutoExpanded = useRef(false);
  const handledExpandActionId = useRef(null);

  // GraphQL mutations for running all tasks by product or article
  const [runAllTasksByProductId] = useMutation(RUN_ALL_TASKS_BY_PRODUCT_ID);
  const [runAllTasksByArticleId] = useMutation(RUN_ALL_TASKS_BY_ARTICLE_ID);

  // Handle running all tasks in a group (product or blog)
  const handleRunGroup = async (groupId) => {
    if (loadingGroupId || runAllTriggered) return;

    if (!authToken || !userId) {
      alert(
        t("common.authRequiredBody", {
          defaultValue: "Please log in again to run these tasks.",
        }),
      );
      return;
    }

    // Find all actions in this group
    const { groups } = groupActions(safeRow.actions);
    const groupActionsList = groups[groupId] || [];

    if (groupActionsList.length === 0) return;

    setRunAllTriggered(true);
    setLoadingGroupId(groupId);
    onRunAllStarted?.();

    try {
      // Get the first action to determine type (product or blog)
      const firstAction = groupActionsList[0];

      // Check if it's a blog or product
      const isBlog = isBlogAction(firstAction);
      const isProduct = shouldGroupProductIntelAction(firstAction);

      // Get the target ID (productId or articleId)
      const targetId = isBlog
        ? firstAction.article_id || firstAction.shopifyArticleId
        : firstAction.product_id;

      if (!targetId) {
        console.error("No productId or articleId found for group:", groupId);
        alert("Unable to run tasks: missing product or article ID.");
        setRunAllTriggered(false);
        setLoadingGroupId(null);
        return;
      }

      let result;
      if (isBlog) {
        result = await runAllTasksByArticleId({
          variables: { userId, articleId: targetId },
          context: {
            headers: { Authorization: `Bearer ${authToken}` },
          },
        });
      } else if (isProduct) {
        result = await runAllTasksByProductId({
          variables: { userId, productId: targetId },
          context: {
            headers: { Authorization: `Bearer ${authToken}` },
          },
        });
      }

      // Check for success and remove group if successful
      const response = isBlog
        ? result?.data?.runAllTasksByArticleId
        : result?.data?.runAllTasksByProductId;

      if (response?.success) {
        // Remove all actions in this group from the list
        for (const action of groupActionsList) {
          handleActionCompleted(action.id);
        }
      } else {
        // Show error message if not successful
        setRunAllTriggered(false);
        alert(response?.message || "Failed to run tasks. Please try again.");
      }
    } catch (err) {
      console.error("Failed to run group:", err?.message || err);
      setRunAllTriggered(false);
      alert(
        t("common.unknownError", {
          defaultValue: "Something went wrong. Please try again.",
        }),
      );
    } finally {
      setLoadingGroupId(null);
    }
  };

  const handleToggleAccordion = (actionId) => {
    setExpandedActionId((prev) => (prev === actionId ? null : actionId));
  };

  // Handle run task callback to notify parent
  const handleRunTask = (actionId) => {
    if (onRunTask) {
      onRunTask(actionId);
    }
  };

  // Remove an action from the list when all its tasks are completed/skipped
  const handleActionCompleted = (actionId) => {
    const updatedActions = safeRow.actions.filter((a) => a.id !== actionId);

    // Update local display
    if (expandedActionId === actionId) {
      setExpandedActionId(null);
    }

    // Notify parent to update the row
    if (onUpdateRow && updatedActions.length !== safeRow.actions.length) {
      onUpdateRow({ ...safeRow, actions: updatedActions });
    }

    // Trigger refetch with a delay to ensure server has processed the task status update
    if (onActionCompleted) {
      setTimeout(() => {
        onActionCompleted();
      }, 1000);
    }
  };

  // Get actions from row prop (can be updated via handleActionCompleted)
  const currentActions = safeRow.actions;
  const groupedCurrentActions = useMemo(() => groupActions(currentActions), [currentActions]);

  const filteredView = useMemo(() => {
    const q = norm(searchQuery);
    const groups = {};
    const groupTitles = {};
    const groupKeys = Object.keys(groupedCurrentActions.groups || {});
    let totalVisibleActions = 0;

    groupKeys.forEach((groupKey) => {
      const groupActionsList = groupedCurrentActions.groups[groupKey] || [];
      const groupTitle = groupedCurrentActions.groupTitles[groupKey];
      const groupTitleMatches = norm(groupTitle || "").includes(q);

      const visibleActions = !q
        ? groupActionsList
        : groupTitleMatches
          ? groupActionsList
          : groupActionsList.filter((action) => actionMatchesQuery(action, q));

      if (!visibleActions.length) return;

      groups[groupKey] = visibleActions;
      groupTitles[groupKey] = groupTitle;
      totalVisibleActions += visibleActions.length;
    });

    return { groups, groupTitles, groupKeys: Object.keys(groups), totalVisibleActions };
  }, [groupedCurrentActions, searchQuery]);

  const visibleActions = useMemo(
    () => Object.values(filteredView.groups).flat(),
    [filteredView.groups]
  );
  const visibleExpandKeys = useMemo(
    () =>
      new Set([
        ...filteredView.groupKeys,
        ...visibleActions.map((action) => action?.id).filter(Boolean),
      ]),
    [filteredView.groupKeys, visibleActions],
  );

  useEffect(() => {
    if (!isOpen) {
      setRunAllTriggered(false);
      setLoadingGroupId(null);
      setSearchQuery("");
      handledExpandActionId.current = null;
      return;
    }
    setSearchQuery(initialSearchQuery || "");
  }, [isOpen, initialSearchQuery]);

  useEffect(() => {
    if (expandedActionId && !visibleExpandKeys.has(expandedActionId)) {
      setExpandedActionId(null);
    }
  }, [expandedActionId, visibleExpandKeys]);

  useEffect(() => {
    // Honor a parent-requested expansion once per modal open / prop change.
    if (
      expandActionId &&
      expandActionId !== handledExpandActionId.current &&
      visibleActions.some((a) => a.id === expandActionId)
    ) {
      handledExpandActionId.current = expandActionId;
      const targetGroupKey =
        filteredView.groupKeys.find((groupKey) =>
          (filteredView.groups[groupKey] || []).some(
            (action) => action?.id === expandActionId,
          ),
        ) || expandActionId;
      setExpandedActionId(targetGroupKey);
      return;
    }

    // Auto-expand first accordion only if expandedActionId is null
    const shouldAutoExpand =
      isOpen &&
      visibleActions.length > 0 &&
      !hasAutoExpanded.current &&
      expandedActionId === null;

    if (shouldAutoExpand) {
      hasAutoExpanded.current = true;
      // Use setTimeout to avoid synchronous setState in effect
      const timer = setTimeout(() => {
        if (filteredView.groupKeys[0]) {
          setExpandedActionId(filteredView.groupKeys[0]);
        } else if (visibleActions[0]?.id) {
          setExpandedActionId(visibleActions[0].id);
        }
      }, 0);
      return () => clearTimeout(timer);
    }
    // Reset when modal closes
    if (!isOpen) {
      hasAutoExpanded.current = false;
    }
  }, [
    isOpen,
    isSuggestedTab,
    isRunningTab,
    currentActions,
    visibleActions,
    filteredView.groupKeys,
    filteredView.groups,
    expandedActionId,
    expandActionId,
  ]);

  const shouldRender = Boolean(isOpen && row);
  if (!shouldRender) return null;

  if (typeof document === "undefined") return null;

  const modal = (
    <div className={styles.modalOverlay} role="presentation" onClick={onClose}>
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-label={safeRow.focusArea || "Focus area details"}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h3 className={styles.modalTitle}>{safeRow.focusArea}</h3>
            <div className={styles.headerToolsRow}>
              {safeRow.pillLabel && (
                <span
                  className={styles.modalPill}
                  style={{ backgroundColor: safeRow.pillColor }}
                >
                  {safeRow.pillLabel}
                </span>
              )}
              <div className={styles.searchRow}>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className={styles.searchInput}
                  placeholder="Search products, blogs, actions, or tasks..."
                  aria-label="Search actions and tasks"
                />
                {searchQuery ? (
                  <button
                    type="button"
                    className={styles.searchClear}
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          <button type="button" className={styles.modalClose} onClick={onClose}>
            X
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.modalMeta}>
            {filteredView.totalVisibleActions} of {currentActions.length} actions
          </div>

          <div className={styles.actionList}>
            {filteredView.groupKeys.length ? (
              filteredView.groupKeys.map((groupKey) => {
                const groupActionsList = filteredView.groups[groupKey];
                const groupTitle = filteredView.groupTitles[groupKey];
                const isProductGroup = groupKey.startsWith("product-");

                if (isProductGroup) {
                  return (
                    <div key={groupKey} className={styles.groupContainer}>
                      <SelectedActionProductGroup
                        groupKey={groupKey}
                        title={groupTitle}
                        actions={groupActionsList}
                        isExpanded={expandedActionId === groupKey}
                        onToggle={() => handleToggleAccordion(groupKey)}
                        activeTab={activeTab}
                        onClose={onClose}
                        onActionCompleted={handleActionCompleted}
                        onRunTask={handleRunTask}
                      />
                    </div>
                  );
                }

                return (
                  <div key={groupKey} className={styles.groupContainer}>
                    {groupTitle && (
                      <div className={styles.groupHeader}>
                        <span className={styles.groupHeaderTitle}>
                          {groupTitle}
                        </span>
                        {isSuggestedTab && (
                          <button
                            type="button"
                            className={styles.runButton}
                            onClick={() => handleRunGroup(groupKey)}
                            disabled={runAllTriggered || loadingGroupId === groupKey}
                          >
                            {loadingGroupId === groupKey || runAllTriggered
                              ? t("SelectedSuggestedActionModal.runningNow") ||
                                "Running..."
                              : t("SelectedSuggestedActionSection.runAll") ||
                                "Run All Tasks"}
                          </button>
                        )}
                      </div>
                    )}
                    {groupActionsList.map((action) => (
                      <SelectedActionItem
                        key={action.id}
                        action={action}
                        groupActions={groupActionsList}
                        taskCount={action.tasks?.length || 0}
                        isExpanded={expandedActionId === action.id}
                        onToggle={() => handleToggleAccordion(action.id)}
                        activeTab={activeTab}
                        onClose={onClose}
                        onActionCompleted={handleActionCompleted}
                        onRunTask={handleRunTask}
                      />
                    ))}
                  </div>
                );
              })
            ) : (
              <div className={styles.emptySearchState}>
                No matching actions or tasks found.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default SelectedActionModal;
